import {
  DARK_MODE_MEDIA_QUERY,
  THEME_ATTRIBUTE,
  THEME_PREFERENCES,
  THEME_STORAGE_KEY,
} from './colorMode.constants';
import type { ResolvedTheme, ThemePreference } from './colorMode.constants';

export const isThemePreference = (value: unknown): value is ThemePreference =>
  typeof value === 'string' &&
  (THEME_PREFERENCES as readonly string[]).includes(value);

export const resolveTheme = (
  preference: ThemePreference,
  systemPrefersDark: boolean
): ResolvedTheme => {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
};

export const getSystemPrefersDark = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(DARK_MODE_MEDIA_QUERY).matches;
};

// localStorage can throw (private mode, blocked storage) — the theme is a
// convenience, so every access fails soft.
export const readStoredPreference = (): ThemePreference | null => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : null;
  } catch {
    return null;
  }
};

export const writeStoredPreference = (preference: ThemePreference) => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Ignore: the server preference still carries the choice.
  }
};

export const clearStoredPreference = () => {
  try {
    window.localStorage.removeItem(THEME_STORAGE_KEY);
  } catch {
    // Ignore.
  }
};

/** `null` removes the attribute, which is the plain light app. */
export const applyThemeToDocument = (theme: ResolvedTheme | null) => {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.setAttribute(THEME_ATTRIBUTE, 'dark');
  } else {
    root.removeAttribute(THEME_ATTRIBUTE);
  }
};

/**
 * Set on `<html>` while the theme flips. A global rule (theme/index.js) turns
 * every CSS transition off under it, so all colours change in one frame:
 * without it, the many components with their own `transition: all 0.2s`
 * animated from the old palette for a moment after the switch, so parts of
 * the page caught up late — the glitch.
 */
export const THEME_SWITCHING_ATTRIBUTE = 'data-theme-switching';

// Two frames: the new styles are committed and painted before transitions
// come back, so nothing animates from the old colours.
const afterNextPaint = (done: () => void) => {
  requestAnimationFrame(() => requestAnimationFrame(done));
};

/**
 * Runs a theme change the way X and Facebook do it: instantly, every colour
 * in the same frame. No crossfade — a fade of a full-page snapshot read as
 * ghosting, and anything with its own transition lagged behind it. Under
 * `THEME_SWITCHING_ATTRIBUTE` no CSS transition runs; `update` applies the
 * theme, a forced style flush commits it with transitions still off, and they
 * come back once the new frame is painted (next-themes'
 * `disableTransitionOnChange`).
 */
export const runThemeTransition = (update: () => void) => {
  const root = document.documentElement;
  root.setAttribute(THEME_SWITCHING_ATTRIBUTE, '');
  update();
  // Flush styles now, while transitions are off, so nothing starts one from
  // the old colours when the attribute goes.
  void window.getComputedStyle(root).color;
  afterNextPaint(() => root.removeAttribute(THEME_SWITCHING_ATTRIBUTE));
};
